// Phase H.13d — TipTap extension that lets headings carry a `class`
// attribute so we can flag a heading as `numbered-section`. CSS counters
// (defined in index.css + the WeasyPrint @page CSS) do the actual
// numbering at render time — the doc never stores the numbers.
//
// We add `class` as a global attribute on the existing heading node
// (rather than swapping out StarterKit's heading) to minimise blast
// radius and avoid touching unrelated heading behaviour.
//
// Toggle behaviour: `toggleNumberedSection` walks up the selection's
// ancestor chain to the nearest `heading` node, then flips the
// `numbered-section` token on/off in that node's class attribute. Other
// classes on the heading (if any are added later) are preserved.

import { Extension } from "@tiptap/core";

const NUMBERED_CLASS = "numbered-section";

function toggleClassToken(existing, token) {
    const cleaned = (existing || "")
        .split(/\s+/)
        .map((c) => c.trim())
        .filter(Boolean);
    const hasToken = cleaned.includes(token);
    const next = hasToken
        ? cleaned.filter((c) => c !== token)
        : [...cleaned, token];
    const joined = next.join(" ").trim();
    return joined.length > 0 ? joined : null;
}

function findHeadingInSelection(state) {
    const { $from } = state.selection;
    for (let d = $from.depth; d >= 0; d--) {
        const node = $from.node(d);
        if (node?.type?.name === "heading") {
            return { node, depth: d };
        }
    }
    return null;
}

export function isNumberedSectionActive(editor) {
    if (!editor) return false;
    try {
        const found = findHeadingInSelection(editor.state);
        if (!found) return false;
        const cls = found.node.attrs.class || "";
        return cls.split(/\s+/).includes(NUMBERED_CLASS);
    } catch (_) {
        return false;
    }
}

export const HeadingClass = Extension.create({
    name: "headingClass",

    addGlobalAttributes() {
        return [
            {
                // Add a free-form `class` attribute to every heading node so
                // we can persist `numbered-section` (and any future per-
                // heading flags) across save/reload round-trips.
                types: ["heading"],
                attributes: {
                    class: {
                        default: null,
                        parseHTML: (el) => el.getAttribute("class") || null,
                        renderHTML: (attrs) => {
                            if (!attrs.class) return {};
                            return { class: attrs.class };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            // Toggle `numbered-section` on the heading currently containing
            // the selection. No-op (returns false) if the selection isn't
            // inside a heading — toolbar UI disables the button in that
            // case, but we still guard here.
            toggleNumberedSection:
                () =>
                ({ state, dispatch, tr }) => {
                    const found = findHeadingInSelection(state);
                    if (!found) return false;

                    const $pos = state.doc.resolve(
                        state.selection.$from.before(found.depth)
                    );
                    const pos = $pos.pos;
                    const node = state.doc.nodeAt(pos);
                    if (!node || node.type.name !== "heading") return false;

                    const nextClass = toggleClassToken(
                        node.attrs.class || "",
                        NUMBERED_CLASS
                    );
                    if (dispatch) {
                        tr.setNodeMarkup(pos, undefined, {
                            ...node.attrs,
                            class: nextClass,
                        });
                        dispatch(tr);
                    }
                    return true;
                },
        };
    },
});

export default HeadingClass;
